'use client'

import { Controller, useFormContext } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { FormControl } from '@/components/form-control'
import { CheckCircle2, MailIcon } from 'lucide-react'
import { emailRegex, emailValidation } from '@/lib/form-validations'
import { VALIDATE_EMAIL } from '@/modules/auth/queries'
import { useDebouncedCallback } from 'use-debounce'
import { useLazyQuery } from '@apollo/client'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { Query } from '@/lib/generated/graphql-types'
import { EMAIL_ERROR_FIELD } from '@/modules/forms/common/constants'
import { ChangeEvent, ClipboardEvent, useEffect } from 'react'
import {
  EmailFieldStatus,
  useEmailFieldStore,
} from '@/modules/forms/common/email-field-store'
import { EmailMessagingStatus } from '@/modules/auth/constants'

const emailMessaging = {
  [EmailMessagingStatus.deliverable]: 'Email looks good!',
  [EmailMessagingStatus.undeliverable]: 'Not a valid email',
  [EmailMessagingStatus.risky]:
    'We may not be able to deliver to this email. Please try another, if possible.',
  [EmailMessagingStatus.unknown]:
    "We couldn't verify this email. Please double check before continuing.",
}

/**
 * Email field that validates the email address and displays a message
 * based on the email's deliverability using Kickbox API.
 *
 * NOTE:
 * We use Zustand to manage the email field's status to determine
 * if the email field has been `called` or if its in a `loading` state.
 *
 * This Zustand Store is built with the assumption that only 1 emailField
 * exists on the page at any given time. This component will have to be
 * refactored if we want to support multiple email fields on the page.
 */
export function EmailField({
  fieldName,
  label,
  subLabel,
  placeholder,
  allowList,
  onValidateEmail,
}: {
  fieldName: string
  label: string
  subLabel?: string
  placeholder?: string
  allowList?: string[]
  onValidateEmail?: (opts: {
    isValidEmail: boolean
    data: Pick<Query, 'validateEmail'>
  }) => void
}) {
  const { control, setError, clearErrors, formState, watch } = useFormContext()
  const emailFieldStatus = useEmailFieldStore((state) => state.emailFieldStatus)
  const setEmailFieldStatus = useEmailFieldStore(
    (state) => state.setEmailFieldStatus,
  )
  const setEmailVerificationStatus = useEmailFieldStore(
    (state) => state.setEmailVerificationStatus,
  )
  const emailFieldValue = watch(fieldName)
  const emailFieldErrorMessage = formState.errors[EMAIL_ERROR_FIELD]?.message

  // previousData is the previous result of the query, and is the boolean of what is
  // returned from the query.
  const [validateEmail, { loading: validateEmailLoading, data, called }] =
    useLazyQuery<Pick<Query, 'validateEmail'>>(VALIDATE_EMAIL, {
      onCompleted: (data) => {
        const isValidEmail = data?.validateEmail?.success ?? false
        onValidateEmail?.({ data, isValidEmail })
        setEmailVerificationStatus(
          data?.validateEmail?.result as EmailMessagingStatus,
        )
        if (isValidEmail) {
          clearErrors(EMAIL_ERROR_FIELD)
          return
        }
        if (
          allowList?.length &&
          allowList.some((allow) => emailFieldValue.endsWith(allow))
        ) {
          clearErrors(EMAIL_ERROR_FIELD)
          return
        }
        setError(EMAIL_ERROR_FIELD, {
          type: 'manual',
          message:
            emailMessaging[
              data?.validateEmail?.result as EmailMessagingStatus
            ] || emailMessaging[EmailMessagingStatus.unknown],
        })
      },
    })

  useEffect(() => {
    return () => {
      setEmailFieldStatus(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // see NOTE in EmailField description above
    if (called) {
      setEmailFieldStatus(EmailFieldStatus.Called)
    }
  }, [called, setEmailFieldStatus])

  const validateEmailResponse =
    data?.validateEmail ?? ({} as Query['validateEmail'])
  const validateEmailDebounced = useDebouncedCallback(validateEmail, 350)
  const didEmailVerificationPass =
    validateEmailResponse.success &&
    emailFieldStatus === EmailFieldStatus.Called

  const showEmailValidationMessages = emailRegex.test(emailFieldValue)

  return (
    <div>
      <Controller
        control={control}
        name={fieldName}
        rules={{
          required: `${label} is required`,
          pattern: emailValidation,
        }}
        render={({ field, fieldState }) => {
          const handleChange = (
            e: ChangeEvent<HTMLInputElement> | ClipboardEvent<HTMLInputElement>,
          ) => {
            const input = (e.target as HTMLInputElement).value
            field.onChange(e)

            if (!input) {
              clearErrors(EMAIL_ERROR_FIELD)
              return
            }

            if (
              allowList?.length &&
              allowList.some((allow) => input.endsWith(allow))
            ) {
              setEmailFieldStatus(EmailFieldStatus.Called)
              clearErrors(EMAIL_ERROR_FIELD)
              return
            }
            if (!emailRegex.test(input)) {
              return
            }
            validateEmailDebounced({
              variables: { email: input },
            })
          }
          return (
            <FormControl
              label={label}
              subLabel={subLabel}
              errorText={
                fieldState.error?.message ||
                (showEmailValidationMessages && !validateEmailLoading
                  ? (emailFieldErrorMessage as string)
                  : undefined)
              }
            >
              <Input
                {...field}
                type="email"
                LeftIcon={MailIcon}
                placeholder={placeholder}
                onPaste={handleChange}
                onChange={handleChange}
              />
            </FormControl>
          )
        }}
      />
      {allowList?.length &&
      allowList.some((allow) => emailFieldValue.endsWith(allow)) ? (
        <div className="flex items-center mt-1">
          <CheckCircle2 className="text-success w-4 h-4" />
          <span className="text-xs text-copy-accent ml-1">
            Skipping email verification
          </span>
        </div>
      ) : null}
      {showEmailValidationMessages && validateEmailLoading ? (
        <div className="flex items-center mt-1">
          <LoadingSpinner size="small" className="text-copy-primary" />
          <span className="text-xs italic text-copy-accent ml-1">
            Verifying email
          </span>
        </div>
      ) : null}
      {showEmailValidationMessages &&
        !validateEmailLoading &&
        didEmailVerificationPass && (
          <div className="flex items-center mt-1">
            <CheckCircle2 className="text-success w-4 h-4" />
            <span className="text-xs text-copy-accent ml-1">Valid email</span>
          </div>
        )}
    </div>
  )
}
